import { FC, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Customer } from 'src/models/Management.interface';
import { getBasicCustomers } from 'src/services/customer.service';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSnackbar } from 'notistack';
import { generateOrderReportPDF } from 'src/components/Invoice/OrderReportPDF';
import { generateOrderReportCSV } from 'src/utils/csv.utils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import MainLayout from 'src/pages/private';
import { useGetOrderReport } from 'src/services/report.service';
import { OrderStatus } from 'src/models/enums/OrderStatus.enum';
import Status from 'src/components/Status';
import Tooltip from '@mui/material/Tooltip';
import {
  StyledHorizontalTabs,
  StyledHorizontalTab,
  TabPanel,
} from 'src/components/StyledComponents/StyledHorizontalTabs';
import {
  ReportPageWrapper,
  FilterPaper,
  FilterActionsBoxSmallGap,
  FlexSpacer,
  GenerateButton,
  PdfIconButton,
  CsvIconButton,
  LoadingBox,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValueNavy,
  StatValueBlue,
  StatValueError,
  StyledTableContainer,
  HeaderCell,
  DataCellBold,
  DataCellMedium,
  DataCellBlue,
  DataCellBalance,
  DataCell,
  EmptyCell,
} from './ReportPage.styled';

const OrderReport: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loading, reportData, fetchOrderReport, error } = useGetOrderReport();
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    Object.values(OrderStatus)
  );

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [tabValue, setTabValue] = useState<number>(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const statusOptions = Object.values(OrderStatus);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await getBasicCustomers();
        setCustomers(data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    loadCustomers();
  }, []);

  const handleGenerateReport = useCallback(() => {
    if (startDate && endDate) {
      fetchOrderReport({
        statuses: selectedStatuses,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        customerId: selectedCustomer?.id,
      });
    }
  }, [
    startDate,
    endDate,
    selectedStatuses,
    selectedCustomer,
    fetchOrderReport,
  ]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Run once on mount — fetchOrderReport is stable (useCallback with [])
  useEffect(() => {
    fetchOrderReport({
      statuses: Object.values(OrderStatus),
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    });
  }, []);

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    if (value.includes('all')) {
      setSelectedStatuses(
        selectedStatuses.length === statusOptions.length ? [] : statusOptions
      );
      return;
    }
    setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '0.00';
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <MainLayout title="Order Report">
      <ReportPageWrapper>
        <FilterPaper elevation={0}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={customers}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return (
                      option.name ||
                      option.organizationName ||
                      ''
                    ).trim();
                  }}
                  value={selectedCustomer}
                  onChange={(_, newValue) => setSelectedCustomer(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      variant="outlined"
                      sx={{
                        '& .MuiInputBase-input': {
                          paddingLeft: '8px !important',
                        },
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-select-label">Order Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    multiple
                    value={selectedStatuses}
                    onChange={handleStatusChange}
                    input={<OutlinedInput label="Order Status" />}
                    renderValue={(selected) =>
                      selected.length === statusOptions.length
                        ? 'All Statuses'
                        : `${selected.length} Selected`
                    }
                  >
                    <MenuItem value="all">
                      <Checkbox
                        checked={
                          selectedStatuses.length === statusOptions.length &&
                          statusOptions.length > 0
                        }
                        indeterminate={
                          selectedStatuses.length > 0 &&
                          selectedStatuses.length < statusOptions.length
                        }
                      />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox
                          checked={selectedStatuses.indexOf(status) > -1}
                        />
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label="Date From"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label="Date To"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FilterActionsBoxSmallGap>
                  <GenerateButton
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    Generate
                  </GenerateButton>
                  <FlexSpacer />
                  <Tooltip title="Export to PDF">
                    <span>
                      <PdfIconButton
                        onClick={() =>
                          reportData &&
                          generateOrderReportPDF(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                            statuses: selectedStatuses,
                            customerName: selectedCustomer
                              ? selectedCustomer.name ||
                                selectedCustomer.organizationName
                              : null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </PdfIconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export to CSV">
                    <span>
                      <CsvIconButton
                        onClick={() =>
                          reportData &&
                          generateOrderReportCSV(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                            statuses: selectedStatuses,
                            customerName: selectedCustomer
                              ? selectedCustomer.name ||
                                selectedCustomer.organizationName
                              : null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <DescriptionIcon fontSize="small" />
                      </CsvIconButton>
                    </span>
                  </Tooltip>
                </FilterActionsBoxSmallGap>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </FilterPaper>

        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : (
          <>
            <SummaryGrid container spacing={3}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL ORDERS
                  </StatLabel>
                  <StatValueNavy variant="h4">
                    {reportData?.summary.totalOrders || 0}
                  </StatValueNavy>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL REVENUE
                  </StatLabel>
                  <StatValueNavy variant="h4">
                    {formatCurrency(reportData?.summary.totalRevenue || 0)}
                  </StatValueNavy>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL RECEIVED
                  </StatLabel>
                  <StatValueBlue variant="h4">
                    {formatCurrency(reportData?.summary.totalPaid || 0)}
                  </StatValueBlue>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL OUTSTANDING
                  </StatLabel>
                  <StatValueError variant="h4">
                    {formatCurrency(reportData?.summary.totalOutstanding || 0)}
                  </StatValueError>
                </StatCard>
              </Grid>
            </SummaryGrid>

            <StyledHorizontalTabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{ mb: 3 }}
            >
              <StyledHorizontalTab label="Orders" selected={tabValue === 0} />
              <StyledHorizontalTab label="Products" selected={tabValue === 1} />
            </StyledHorizontalTabs>

            <TabPanel value={tabValue} index={0}>
              <StyledTableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>ORDER #</HeaderCell>
                      <HeaderCell>DATE &amp; TIME</HeaderCell>
                      <HeaderCell>CUSTOMER</HeaderCell>
                      <HeaderCell>STATUS</HeaderCell>
                      <HeaderCell align="right">AMOUNT</HeaderCell>
                      <HeaderCell align="right">RECEIVED AMOUNT</HeaderCell>
                      <HeaderCell align="right">OUTSTANDING AMOUNT</HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData?.orders.length === 0 ? (
                      <TableRow>
                        <EmptyCell colSpan={7} align="center">
                          <Typography color="var(--subtext)">
                            No orders found for the selected criteria.
                          </Typography>
                        </EmptyCell>
                      </TableRow>
                    ) : (
                      reportData?.orders.map((order) => {
                        const balance = order.grandTotal - order.paidAmount;
                        return (
                          <TableRow key={order.id} hover>
                            <DataCellBold>{order.orderNumber}</DataCellBold>
                            <DataCell>
                              {dayjs(order.createdAt).format(
                                'DD/MM/YYYY HH:mm'
                              )}
                            </DataCell>
                            <DataCell>{order.customerName}</DataCell>
                            <DataCell>
                              <Status status={order.status} />
                            </DataCell>
                            <DataCellMedium align="right">
                              {formatCurrency(order.grandTotal)}
                            </DataCellMedium>
                            <DataCellBlue align="right">
                              {formatCurrency(order.paidAmount)}
                            </DataCellBlue>
                            <DataCellBalance
                              align="right"
                              positive={balance > 0}
                            >
                              {formatCurrency(balance)}
                            </DataCellBalance>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <StyledTableContainer sx={{ mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>PRODUCT NAME</HeaderCell>
                      <HeaderCell align="right">QUANTITY SOLD</HeaderCell>
                      <HeaderCell align="right">TOTAL AMOUNT</HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!reportData?.productsSold ||
                    reportData.productsSold.length === 0 ? (
                      <TableRow>
                        <EmptyCell colSpan={3} align="center">
                          <Typography color="var(--subtext)">
                            No products found for the selected criteria.
                          </Typography>
                        </EmptyCell>
                      </TableRow>
                    ) : (
                      reportData.productsSold.map((product) => {
                        return (
                          <TableRow key={product.productId} hover>
                            <DataCell>{product.productName}</DataCell>
                            <DataCellMedium align="right">
                              {product.quantitySold.toLocaleString('en-US')}
                            </DataCellMedium>
                            <DataCellBlue align="right">
                              {formatCurrency(product.totalRevenue)}
                            </DataCellBlue>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </TabPanel>
          </>
        )}
      </ReportPageWrapper>
    </MainLayout>
  );
};

export default OrderReport;
